import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, NAV_SECTION, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Segments from "@/pages/Segments";
import Loyalty from "@/pages/Loyalty";
import Transactions from "@/pages/Transactions";
import ApiSettings from "@/pages/ApiSettings";
import Basket from "@/pages/Basket";
import AiAnalyst from "@/pages/AiAnalyst";
import Promotions from "@/pages/Promotions";
import Stores from "@/pages/Stores";
import Clusters from "@/pages/Clusters";
import Campaigns from "@/pages/Campaigns";
import Reports from "@/pages/Reports";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import Circuits from "@/pages/Circuits";
import Flyers from "@/pages/Flyers";
import Gifts from "@/pages/Gifts";
import FlyerDetail from "@/pages/FlyerDetail";
import FlyerCompare from "@/pages/FlyerCompare";
import FidelityCompare from "@/pages/FidelityCompare";
import KpiCards from "@/pages/KpiCards";
import Tickets from "@/pages/Tickets";
import Manual from "@/pages/Manual";
import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Embed from "@/pages/Embed";
import ProfileLanding from "@/pages/ProfileLanding";
import { endEmbed, inIframe, isEmbed, trustedParent } from "@/lib/embed";
import { loginPath } from "@/lib/tenant";
import { FilterProvider } from "@/context/FilterContext";

const Gate = ({ id, children }) => {
  const { user } = useAuth();
  const ok = id === "circuiti" ? user?.global_admin : id === "superadmin" ? user?.role === "superadmin" : (user?.permissions?.[NAV_SECTION[id]] || "none") !== "none";
  if (ok) return children;
  const first = Object.entries(NAV_SECTION).find(([, s]) => (user?.permissions?.[s] || "none") !== "none");
  if (id === "dashboard" && first) return <Navigate to={first[0] === "impostazioni" ? "/impostazioni-api" : `/${first[0]}`} replace />;
  return <div data-testid="access-denied" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">Il tuo profilo non ha accesso a questa sezione. Contatta l'amministratore.</div>;
};

const FrameBlocked = ({ expired }) => (
  <div className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center" data-testid={expired ? "embed-expired" : "frame-blocked"}>
    <div className="max-w-sm">
      <h1 className="font-heading text-lg font-semibold text-slate-900">{expired ? "Sessione scaduta" : "Apertura non consentita"}</h1>
      <p className="mt-2 text-sm text-slate-600">{expired ? "La sessione del pannello incorporato è terminata: ricarica la pagina dell'applicativo per accedere di nuovo." : "Nouvelle CRM può essere incorporato in un altro sito solo tramite l'integrazione iframe con accesso automatico configurata dal SuperAdmin."}</p>
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  const [expired, setExpired] = useState(false);
  useEffect(() => { const h = () => setExpired(true); window.addEventListener("crm-embed-expired", h); return () => window.removeEventListener("crm-embed-expired", h); }, []);
  if (inIframe() && !isEmbed() && !trustedParent()) return <FrameBlocked />;
  if (isEmbed() && (expired || user === false)) { endEmbed(); return <FrameBlocked expired />; }
  if (user === null) return <div className="grid min-h-screen place-items-center text-sm text-slate-400">Caricamento...</div>;
  if (!user && window.location.pathname === "/") return <Landing />;
  if (!user) return <Navigate to={loginPath()} replace state={{ from: `${window.location.pathname}${window.location.search}` }} />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <FilterProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/:slug" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/embed/:slug" element={<Embed />} />
          <Route path="/embed" element={<Embed />} />
          <Route path="/:circuit/:profile" element={<ProfileLanding />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Gate id="dashboard"><Dashboard /></Gate>} />
            <Route path="/clienti" element={<Gate id="clienti"><Customers /></Gate>} />
            <Route path="/clienti/:id" element={<Gate id="clienti"><CustomerDetail /></Gate>} />
            <Route path="/kpi-carte" element={<Gate id="kpi-carte"><KpiCards /></Gate>} />
            <Route path="/segmenti" element={<Gate id="segmenti"><Segments /></Gate>} />
            <Route path="/fedelta" element={<Gate id="fedelta"><Loyalty /></Gate>} />
            <Route path="/transazioni" element={<Gate id="transazioni"><Transactions /></Gate>} />
            <Route path="/paniere" element={<Gate id="paniere"><Basket /></Gate>} />
            <Route path="/analisi-ia" element={<Gate id="analisi-ia"><AiAnalyst /></Gate>} />
            <Route path="/promozioni" element={<Gate id="promozioni"><Promotions /></Gate>} />
            <Route path="/punti-vendita" element={<Gate id="punti-vendita"><Stores /></Gate>} />
            <Route path="/carta-fedelta" element={<Gate id="carta-fedelta"><FidelityCompare /></Gate>} />
            <Route path="/cluster" element={<Gate id="cluster"><Clusters /></Gate>} />
            <Route path="/comunicazioni" element={<Gate id="comunicazioni"><Campaigns /></Gate>} />
            <Route path="/report" element={<Gate id="report"><Reports /></Gate>} />
            <Route path="/superadmin" element={<Gate id="superadmin"><SuperAdmin /></Gate>} />
            <Route path="/volantini-promo" element={<Gate id="volantini-promo"><Flyers /></Gate>} />
            <Route path="/premi" element={<Gate id="premi"><Gifts /></Gate>} />
            <Route path="/volantini-promo/confronto" element={<Gate id="volantini-promo"><FlyerCompare /></Gate>} />
            <Route path="/volantini-promo/:id" element={<Gate id="volantini-promo"><FlyerDetail /></Gate>} />
            <Route path="/circuiti" element={<Gate id="circuiti"><Circuits /></Gate>} />
            <Route path="/assistenza" element={<Gate id="assistenza"><Tickets /></Gate>} />
            <Route path="/manuale" element={<Gate id="manuale"><Manual /></Gate>} />
            <Route path="/manuale/:id" element={<Gate id="manuale"><Manual /></Gate>} />
            <Route path="/impostazioni-api" element={<Gate id="impostazioni"><ApiSettings /></Gate>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
      </FilterProvider>
    </AuthProvider>
  );
}

export default App;
